// Payload type is now part of the Legend component props
type Payload = { value?: any; id?: any; type?: string; color?: string; [key: string]: any };
import type { CostByInitiativeResponse } from '../../../../api/financials-client/financials-client.type';
import { skyBase } from '../../../../styles/design-tokens';

type DataPoint = {
  date: string;
  [key: string]: string | number;
};

/**
 * Filters the cost data based on optional initiative IDs and/or team ID
 *
 * @param {CostByInitiativeResponse[] | undefined} data - The raw cost data from the API
 * @param {string[] | undefined} initiativeIds - Optional array of initiative IDs to include
 * @param {string | undefined} teamId - Optional team ID to filter by
 * @returns {CostByInitiativeResponse[]} Filtered cost data
 */
const filterCostByInitiativeData = (
  data: CostByInitiativeResponse[] | undefined,
  initiativeIds?: string[],
  teamId?: string,
): CostByInitiativeResponse[] => {
  if (!data) return [];

  let filteredData = [...data];

  // Filter by initiative IDs if provided
  if (initiativeIds && initiativeIds.length > 0) {
    filteredData = filteredData.filter((item) => initiativeIds.includes(item.id));
  }

  // Filter by team ID if provided
  if (teamId) {
    filteredData = filteredData
      .map((initiative) => {
        const newInitiative = { ...initiative };
        // Filter the teams array to only include the specified team
        const filteredTeams = initiative.chart_data.teams.filter((team) => team.id === teamId);

        // If after filtering there are no teams left, return null to exclude this initiative
        if (filteredTeams.length === 0) {
          return null;
        }

        // Replace the initiative costs with the specific team's costs
        // This ensures we only return team data, not total data
        newInitiative.chart_data = {
          initiative: filteredTeams[0].costs, // Use the team costs instead of initiative totals
          teams: filteredTeams,
        };

        return newInitiative;
      })
      .filter(Boolean) as CostByInitiativeResponse[];
  }

  return filteredData;
};

/**
 * Transforms the filtered cost data into a format that charts can use
 *
 * @param {CostByInitiativeResponse[]} data The filtered cost data
 * @returns {DataPoint[]} Data formatted for chart
 */
const transformCostByInitiativeData = (data: CostByInitiativeResponse[]): DataPoint[] => {
  // Find all unique dates across all initiatives
  const allDates = new Set<string>();
  data.forEach((initiative) => {
    Object.keys(initiative.chart_data.initiative).forEach((date) => {
      allDates.add(date);
    });
  });

  // Sort dates chronologically
  const sortedDates = Array.from(allDates).sort();
  const line: DataPoint[] = sortedDates.map((date) => {
    const dataPoint: DataPoint = { date };

    // Add data for each initiative
    data.forEach((initiative) => {
      const value = initiative.chart_data.initiative[date] || 0;
      dataPoint[initiative.id] = value;
    });

    return dataPoint;
  });

  return line;
};

/**
 * Helper function to get an initiative name & id map from the data
 *
 * @param {CostByInitiativeResponse[] | undefined} data The raw cost data from the API
 * @returns {Record<string, string>} A map of initiative id to initiative name
 */
function getInitiativeInfo(data: CostByInitiativeResponse[] | undefined): { [id: string]: string } {
  if (!data) return {};
  return data.reduce(
    (acc, initiative) => {
      acc[initiative.id] = initiative.name;
      return acc;
    },
    {} as { [id: string]: string },
  );
}

/**
 * Helper function to get the color for a specific initiative
 *
 * @param {string} itemId The item ID
 * @param {string[]} selectedItemIds The list of selected item IDs
 * @param {string[]} colorsList The list of colors to use for the items
 * @returns {string} The color for the item
 */
function getItemColor(itemId: string, selectedItemIds: string[], colorsList: string[]): string {
  const index = selectedItemIds.findIndex((id) => id === itemId);
  return index <= colorsList.length - 1 ? colorsList[index] : skyBase;
}

/**
 * Helper function to get all available team IDs from the data
 *
 * @param {CostByInitiativeResponse[]} data The raw cost data from the API
 * @returns {Array<{id: string; name: string}>} Array of team IDs with their names
 */
const getTeamInfo = (data: CostByInitiativeResponse[]): { id: string; name: string }[] => {
  const teams = new Map<string, string>();

  data.forEach((initiative) => {
    initiative.chart_data.teams.forEach((team) => {
      teams.set(team.id, team.name);
    });
  });

  return Array.from(teams.entries()).map(([id, name]) => ({ id, name }));
};

/**
 * Helper function to build the legend payload for the cost by initiative chart
 *
 * @param {string[]} initiativeIds The initiative IDs to include in the legend
 * @param {Record<string, string>} initiativeInfo Map of initiative IDs to names
 * @param {string[]} colorsList The list of colors to use for the initiatives
 * @returns {Payload[]} The legend payload
 */
const buildChartLegend = (
  initiativeIds: string[],
  initiativeInfo: { [id: string]: string },
  colorsList: string[],
): Payload[] => {
  return initiativeIds.map((id, _, ids) => ({
    id,
    value: initiativeInfo[id],
    color: getItemColor(id, ids, colorsList),
  }));
};

export {
  buildChartLegend,
  filterCostByInitiativeData,
  getInitiativeInfo,
  getItemColor,
  getTeamInfo,
  transformCostByInitiativeData,
};
